import type { Manufacturer } from "../../../domain/types/machine-product";

/**
 * 제조사별 SNMP OID 추천값.
 *
 * 지어낸 값이 아니라 **이미 등록되어 동작 중인 제품들에서 뽑은 것**이다.
 * 제조사가 같으면 OID 가 거의 같다는 성질을 이용한다 — 실제로 후지제록스·후지필름은
 * 모든 필드가 한 값으로 통일돼 있고, 코니카미놀타도 23개 중 22개가 같다.
 * 값이 갈리는 필드(리코·HP 일부)는 **가장 많이 쓰인 값**을 골랐다.
 *
 * 어디까지나 출발점이다. 모델에 따라 다를 수 있으니 등록 전에 확인하고 고치면 된다.
 *
 * 갱신하려면 machine_product 에서 제조사별 최빈값을 다시 뽑으면 된다.
 *
 * 기준 제품 수: RICOH 17개, FUJI_XEROX 15개, KONICA_MINOLTA 15개, HP 9개, FUJI_FILM 2개, OKI 1개, SINDOH 1개
 */
export const OID_PRESETS: Partial<Record<Manufacturer, Record<string, string>>> = {
  RICOH: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.2.1.43.10.2.1.4.1.1",
    suppliesNameCommand: "1.3.6.1.2.1.43.11.1.1.6.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.2.1.43.8.2.1.13.1",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.9.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.8.2.1.10.1",
    jobNameCommand: "1.3.6.1.4.1.367.3.2.1.3.2.1.1.1.4",
    jobStartTimeCommand: "1.3.6.1.4.1.367.3.2.1.3.2.1.1.1.8",
    jobEndTimeCommand: "1.3.6.1.4.1.18334.1.1.4.1.3.1.1.7.1",
    jobStatusCommand: "1.3.6.1.4.1.367.3.2.1.3.2.1.1.1.11",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
  FUJI_XEROX: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.2.1.43.10.2.1.4.1.1",
    suppliesNameCommand: "1.3.6.1.2.1.43.11.1.1.6.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.2.1.43.8.2.1.13.1",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.9.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.8.2.1.10.1",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
  KONICA_MINOLTA: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.2.1.43.10.2.1.4.1.1",
    suppliesNameCommand: "1.3.6.1.2.1.43.11.1.1.6.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.4.1.18334.1.1.1.5.6.2.1.3",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.9.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.8.2.1.10.1",
    jobNameCommand: "1.3.6.1.4.1.18334.1.1.4.1.3.1.1.12.1",
    jobStartTimeCommand: "1.3.6.1.4.1.18334.1.1.4.1.3.1.1.6.1",
    jobEndTimeCommand: "1.3.6.1.4.1.18334.1.1.4.1.3.1.1.7.1",
    jobStatusCommand: "1.3.6.1.4.1.18334.1.1.4.1.3.1.1.3.1",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
  HP: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.4.1.11.2.3.9.4.2.1.4.1.2.5.0",
    suppliesNameCommand: "1.3.6.1.2.1.43.12.1.1.4.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.2.1.43.8.2.1.13.1",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.11.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.11.1.1.3.1",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
  FUJI_FILM: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.2.1.43.10.2.1.4.1.1",
    suppliesNameCommand: "1.3.6.1.2.1.43.11.1.1.6.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.2.1.43.8.2.1.13.1",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.9.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.8.2.1.10.1",
    jobNameCommand: "1.3.6.1.4.1.297.1.111.1.51.1.1.9.2",
    jobStartTimeCommand: "1.3.6.1.4.1.297.1.111.1.51.1.1.5.2",
    jobEndTimeCommand: "1.3.6.1.4.1.297.1.111.1.51.1.1.6.2",
    jobStatusCommand: "1.3.6.1.4.1.297.1.111.1.51.1.1.11.2",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
  OKI: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.2.1.43.10.2.1.4.1.1",
    suppliesNameCommand: "1.3.6.1.2.1.43.11.1.1.6.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.2.1.43.8.2.1.13.1",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.9.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.8.2.1.10.1",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
  SINDOH: {
    machineStatusCommand: "1.3.6.1.2.1.25.3.5.1.1.1",
    machineRuntimeCommand: "1.3.6.1.2.1.1.3.0",
    machineTotalWorkloadCommand: "1.3.6.1.2.1.43.10.2.1.4.1.1",
    suppliesNameCommand: "1.3.6.1.2.1.43.11.1.1.6.1",
    suppliesTypeCommand: "1.3.6.1.2.1.43.11.1.1.5.1",
    suppliesMaxLevelCommand: "1.3.6.1.2.1.43.11.1.1.8.1",
    suppliesCurrentLevelCommand: "1.3.6.1.2.1.43.11.1.1.9.1",
    trayNameCommand: "1.3.6.1.2.1.43.8.2.1.13.1",
    paperNameCommand: "1.3.6.1.2.1.43.8.2.1.12.1",
    paperWidthCommand: "1.3.6.1.2.1.43.8.2.1.7.1",
    paperHeightCommand: "1.3.6.1.2.1.43.8.2.1.5.1",
    trayMaxCapacityCommand: "1.3.6.1.2.1.43.8.2.1.9.1",
    trayCurrentCapacityCommand: "1.3.6.1.2.1.43.8.2.1.10.1",
    alertSeverityCommand: "1.3.6.1.2.1.43.18.1.1.2",
    alertGroupCommand: "1.3.6.1.2.1.43.18.1.1.4",
    alertCodeCommand: "1.3.6.1.2.1.43.18.1.1.7",
    alertDescriptionCommand: "1.3.6.1.2.1.43.18.1.1.8",
    alertTimeCommand: "1.3.6.1.2.1.43.18.1.1.9",
    errorStateCommand: "1.3.6.1.2.1.25.3.5.1.2",
  },
};

/** 추천값이 몇 개 제품을 근거로 만들어졌는지 (화면 안내용). */
export const OID_PRESET_SOURCE_COUNT: Partial<Record<Manufacturer, number>> = {
  RICOH: 17,
  FUJI_XEROX: 15,
  KONICA_MINOLTA: 15,
  HP: 9,
  FUJI_FILM: 2,
  OKI: 1,
  SINDOH: 1,
};
